import React, { createContext, useState, useCallback, useMemo, useContext, useRef } from 'react';
import api from '../api';

const SavedPointsContext = createContext({
  getPointsByType: () => [],
  refreshPointsByType: () => Promise.resolve([]),
  loading: {},
});

/**
 * Provides a shared cache for saved-points data across all pages.
 * Instead of each page independently fetching /saved-points on mount,
 * they call getPointsByType(type) which returns cached data or fetches once.
 */
export function SavedPointsProvider({ children }) {
  // Cache: { [type]: data[] }
  const [pointsCache, setPointsCache] = useState({});
  const [loading, setLoading] = useState({});

  // Track in-flight requests to avoid duplicate fetches (e.g. from StrictMode double-mount)
  const inflightRef = useRef({});

  const fetchPointsByType = useCallback(async (type) => {
    // If already fetching this type, return the existing promise
    if (inflightRef.current[type]) {
      return inflightRef.current[type];
    }

    const fetchPromise = (async () => {
      setLoading((prev) => ({ ...prev, [type]: true }));
      try {
        const params = type ? { type } : {};
        const res = await api.get('/saved-points', { params });
        const data = res.data || [];
        setPointsCache((prev) => ({ ...prev, [type]: data }));
        return data;
      } catch (err) {
        console.error(`Failed to load saved points (type=${type})`, err);
        return [];
      } finally {
        setLoading((prev) => ({ ...prev, [type]: false }));
        delete inflightRef.current[type];
      }
    })();

    inflightRef.current[type] = fetchPromise;
    return fetchPromise;
  }, []);

  /**
   * Get saved points by type. Returns cached data if available,
   * otherwise triggers a fetch and returns the result.
   */
  const getPointsByType = useCallback(
    async (type = '') => {
      if (pointsCache[type] !== undefined) {
        return pointsCache[type];
      }
      return fetchPointsByType(type);
    },
    [pointsCache, fetchPointsByType]
  );

  /**
   * Force refresh saved points for a given type (e.g. after creating/editing a point).
   */
  const refreshPointsByType = useCallback(
    async (type = '') => {
      // Clear cache for this type so it re-fetches
      setPointsCache((prev) => {
        const next = { ...prev };
        delete next[type];
        return next;
      });
      return fetchPointsByType(type);
    },
    [fetchPointsByType]
  );

  const contextValue = useMemo(
    () => ({ getPointsByType, refreshPointsByType, loading, pointsCache }),
    [getPointsByType, refreshPointsByType, loading, pointsCache]
  );

  return (
    <SavedPointsContext.Provider value={contextValue}>
      {children}
    </SavedPointsContext.Provider>
  );
}

/**
 * Hook to access saved points from the shared cache.
 * Usage:
 *   const { points, loading, refresh } = useSavedPoints('procurement_destination');
 */
export function useSavedPoints(type = '') {
  const { getPointsByType, refreshPointsByType, loading, pointsCache } = useContext(SavedPointsContext);
  const [points, setPoints] = useState(pointsCache[type] || []);
  const hasFetchedRef = useRef(false);

  // Fetch on first use if not cached
  React.useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // If already cached, use cached data
    if (pointsCache[type] !== undefined) {
      setPoints(pointsCache[type]);
      return;
    }

    getPointsByType(type).then((data) => {
      setPoints(data);
    });
  }, [type, getPointsByType, pointsCache]);

  // Sync with cache updates (e.g. after refresh)
  React.useEffect(() => {
    if (pointsCache[type] !== undefined) {
      setPoints(pointsCache[type]);
    }
  }, [pointsCache, type]);

  const refresh = useCallback(() => {
    hasFetchedRef.current = false;
    return refreshPointsByType(type).then((data) => {
      setPoints(data);
      return data;
    });
  }, [refreshPointsByType, type]);

  return {
    points,
    loading: loading[type] || false,
    refresh,
  };
}

export default SavedPointsContext;
